import React, { useMemo } from "react";
import { parseISO, format } from "date-fns";
import { IntlShape, useIntl } from "react-intl";
import { RendererProps } from "../shared/renderers";
import {
  asUTC,
  convertUTCToEstablishmentTime,
  DEFAULT_FORMAT_DATE_TIME,
} from "../../lib/dateUtils";
import { DEFAULT_TIME_ZONE } from "../propertyInfo/domain/property.entity";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import { StyledIcon } from "../reservationDetail/components/styles";
import {
  CancellationPolicyEntity,
  PenaltyBracket,
} from "../automaticCharge/infraestructure/automaticCharges.dto";

export function CustomDateTimeRenderer({
  value: valueReq = "",
  config = {},
}: RendererProps<string>) {
  const intl = useIntl();
  const messages = {
    tooltip: {
      id: "customDateTimeRender.tooltip",
      defaultMessage: "Date in timezone: {timeZone}, your local time: {browserDate}",
    },
  };
  const { timeZone = DEFAULT_TIME_ZONE } = config;

  const [establishmentDate, browserDate] = useMemo(() => {
    if (!valueReq) return ["", ""];

    const establishmentFormattedDate = convertUTCToEstablishmentTime(valueReq, timeZone);
    const browserFormattedDate = format(new Date(valueReq), DEFAULT_FORMAT_DATE_TIME);

    return [establishmentFormattedDate, browserFormattedDate];
  }, [valueReq, timeZone]);

  return (
    <>
      {establishmentDate}
      {establishmentDate !== browserDate && (
        <span className="ml-2">
          <OverlayTrigger
            placement="top"
            overlay={
              <Tooltip id="error-tooltip">
                {intl.formatMessage(messages.tooltip, {
                  timeZone,
                  browserDate,
                })}
              </Tooltip>
            }>
            <StyledIcon className="fa fa-info-circle" />
          </OverlayTrigger>
        </span>
      )}
    </>
  );
}

const getRowRange = (
  penaltyElement: PenaltyBracket,
  startDate: string,
  startHour: string,
  endDate: string,
  endHour: string,
  intlLib: IntlShape,
  messages: Record<string, Record<string, string>>
) => {
  if (!penaltyElement.startDate) {
    return intlLib.formatMessage(messages.untilRange, {
      dateTwo: `${endDate} ${endHour}`,
    });
  } else if (!penaltyElement.endDate) {
    return intlLib.formatMessage(messages.fromRange, {
      dateOne: `${startDate} ${startHour}`,
    });
  } else {
    return `${startDate} ${startHour} - ${endDate} ${endHour}`;
  }
};

export const mapPenaltyBrackets = (
  intlLib: IntlShape,
  penaltyBrackets: PenaltyBracket[],
  activeCurrency: string,
  exchangeRate: number,
  rooms: number,
  cancellationMessages: Record<string, Record<string, string>>,
  penaltyRooms?: number[]
) => {
  return penaltyBrackets?.map((item, index) => {
    const formattedStartDate = item.startDate
      ? intlLib.formatDate(new Date(item.startDate), {
          month: "short",
          day: "numeric",
          year: "numeric",
        })
      : "";
    const formattedEndDate = item.endDate
      ? intlLib.formatDate(new Date(item.endDate), {
          month: "short",
          day: "numeric",
          year: "numeric",
        })
      : "";
    const startDate = item.startDate ? formattedStartDate : "";
    const startHour = item.startHour ? `${item.startHour}h*` : "";
    const endDate = item.endDate ? formattedEndDate : "";
    const endHour = item.endHour ? `${item.endHour}h*` : "";
    const description =
      item.type === "CHARGE_FIRST_NIGHT"
        ? `Primera noche`
        : item.type === "PERCENTAGE"
        ? ` (${item?.penaltyBase}%)`
        : "";
    const finalRooms = penaltyRooms && penaltyRooms[index] ? penaltyRooms[index] : rooms;

    return {
      cellOne: getRowRange(
        item,
        startDate,
        startHour,
        endDate,
        endHour,
        intlLib,
        cancellationMessages
      ),
      cellTwo:
        `${activeCurrency} ` +
        (item?.penalty ? intlLib.formatNumber(item.penalty * exchangeRate * finalRooms) : "") +
        description,
      className: item?.penalty === 0 ? "text-gray-200 !font-bold" : "",
    };
  });
};

export function CancellationPolicyDetailsRenderer({
  value: valueReq,
  config = {},
}: RendererProps<CancellationPolicyEntity>) {
  const intl = useIntl();
  if (!valueReq) return null;
  const cancellationMessages: Record<string, Record<string, string>> = {
    untilRange: {
      id: "renders.cancellationPolicies.until",
      description: "Until {dateTwo}",
      defaultMessage: "Until {dateTwo}",
    },
    fromRange: {
      id: "renders.cancellationPolicies.from",
      description: "From {dateOne}",
      defaultMessage: "From {dateOne}",
    },
  };

  const { penaltyBrackets = [], conditionTicker, penaltyRooms, rooms } = valueReq || {};

  const activeCurrency = config.defaultCurrency || "€";
  const exchangeRate = 1;

  const gridContent = mapPenaltyBrackets(
    intl,
    penaltyBrackets,
    activeCurrency,
    exchangeRate,
    rooms,
    cancellationMessages,
    penaltyRooms
  );
  const gridHeaders: [string, string] = [
    intl.formatMessage({
      id: "automaticCharges.cancellationPolicies.range",
      defaultMessage: "Rango",
    }),
    intl.formatMessage({
      id: "automaticCharges.cancellationPolicies.penalty",
      defaultMessage: "Penalización",
    }),
  ];

  return (
    <div>
      <span className="mr-2">{conditionTicker}</span>
      <OverlayTrigger
        placement="top"
        overlay={
          <Tooltip
            id=""
            style={{
              minWidth: 500,
              maxWidth: 500,
              width: 500,
              background: "#222",
              color: "#fff",
            }}>
            <div style={{ width: "100%" }}>
              <table
                style={{
                  background: "#222",
                  color: "#fff",
                  minWidth: 480,
                  maxWidth: 480,
                }}
                className="text-xs">
                <thead>
                  <tr>
                    <th
                      style={{
                        fontWeight: 600,
                        padding: "8px 12px",
                        borderBottom: "1px solid #444",
                        color: "#fff",
                        background: "#333",
                      }}>
                      {gridHeaders[0]}
                    </th>
                    <th
                      style={{
                        fontWeight: 600,
                        padding: "8px 12px",
                        borderBottom: "1px solid #444",
                        color: "#fff",
                        background: "#333",
                      }}>
                      {gridHeaders[1]}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {gridContent.map((row, idx) => (
                    <tr key={idx}>
                      <td
                        style={{
                          padding: "8px 12px",
                          borderBottom: idx === gridContent.length - 1 ? "none" : "1px solid #444",
                          color: row.className ? "#aaa" : "#fff",
                          background: "#222",
                        }}
                        className={row.className}>
                        {row.cellOne}
                      </td>
                      <td
                        style={{
                          padding: "8px 12px",
                          borderBottom: idx === gridContent.length - 1 ? "none" : "1px solid #444",
                          color: row.className ? "#aaa" : "#fff",
                          background: "#222",
                        }}
                        className={row.className}>
                        {row.cellTwo}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Tooltip>
        }>
        <StyledIcon className="fa fa-info-circle" />
      </OverlayTrigger>
    </div>
  );
}

export function CustomDateRenderer({ value: valueReq = "" }: RendererProps<string>) {
  const date = asUTC(parseISO(valueReq), "yyyy-MM-dd");
  return <>{date}</>;
}

export function CustomEventTypeRenderer({ value: valueReq = "" }: RendererProps<string>) {
  const intl = useIntl();
  const messages = {
    CREATE: {
      id: "reservationHistory.eventType.create",
      defaultMessage: "CREATE",
    },
    UPDATE: {
      id: "reservationHistory.eventType.update",
      defaultMessage: "UPDATE",
    },
    CANCEL: {
      id: "reservationHistory.eventType.cancel",
      defaultMessage: "CANCEL",
    },
    REACTIVATE: {
      id: "reservationHistory.eventType.reactivate",
      defaultMessage: "REACTIVATE",
    },
  };

  switch (valueReq) {
    case "CREATE":
      return <>{intl.formatMessage(messages.CREATE)}</>;
    case "UPDATE":
      return <>{intl.formatMessage(messages.UPDATE)}</>;
    case "CANCEL":
      return <>{intl.formatMessage(messages.CANCEL)}</>;
    case "REACTIVATE":
      return <>{intl.formatMessage(messages.REACTIVATE)}</>;
    default:
      throw new Error("Invalid event type translation");
  }
}
